import {useForceRemountOnChange} from '@wandb/weave/common/util/hooks';
import {assertUnreachable} from '@wandb/weave/common/util/types';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import React, {useCallback, useEffect, useMemo, useRef} from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Input} from 'semantic-ui-react';

import * as Filters from '../../../util/filters';
import {EMPTY_FILTER_VALUE} from '../../../util/filtersDefaults';
import type * as FilterTypes from '../../../util/filterTypes';
import * as Run from '../../../util/runs';
import type * as RunTypes from '../../../util/runTypes';
import {tryJSONParse} from '../../../util/try_json_parse';
import {useDebouncedValue} from '../../../util/useDebouncedValue';
import {
  type FilterValueSelectorCreatorProps,
  FilterValueSelectorDate,
  FilterValueSelectorTime,
} from '../../Filters/FilterValueSelector';
import {FilterSimpleValueInfiniteDropdown} from '../../InfiniteScrollDropdown/FilterSimpleValueComponents';
import {getCurrentSelections} from '../../InfiniteScrollDropdown/utils';
import {RunsFilterTagNameSelector} from './RunsFilterTagNameSelector';

export type RunFilterValueSelectorCreatorProps =
  FilterValueSelectorCreatorProps<RunTypes.Key>;

type RunFilterValueSelectorProps = RunFilterValueSelectorCreatorProps & {
  entityName: string;
  projectName: string;
};

export function RunsFilterContainsInput({
  filter,
  setFilter,
}: {
  filter: FilterTypes.IndividualFilter<RunTypes.Key>;
  setFilter: (f: FilterTypes.IndividualFilter<RunTypes.Key>) => void;
}) {
  const initialValue = String(filter.value ?? '');
  const filterRef = useRef(filter);
  const setFilterRef = useRef(setFilter);
  filterRef.current = filter;
  setFilterRef.current = setFilter;
  const onDebouncedChange = useCallback((value: string) => {
    setFilterRef.current({
      ...filterRef.current,
      value,
      disabled: value === '',
    } as FilterTypes.IndividualFilter<RunTypes.Key>);
  }, []);
  const {value, debouncedSetValue, instantSetValue} = useDebouncedValue(
    initialValue,
    300,
    onDebouncedChange
  );

  useEffect(() => {
    instantSetValue(initialValue);
  }, [initialValue, instantSetValue]);

  return (
    <Input
      className="filter-list__value"
      data-test="filter-value"
      placeholder="Substring to match"
      value={value}
      onChange={(_, {value: v}) => debouncedSetValue(v ?? '')}
      style={{flexGrow: 1, maxWidth: 400}}
    />
  );
}

export const RunsFilterValueSelector = ({
  entityName,
  projectName,
  filter,
  setFilter,
  isValid = true,
}: RunFilterValueSelectorProps) => {
  const setPartial = (
    partialFilter: Partial<FilterTypes.IndividualFilter<RunTypes.Key>>
  ) => {
    setFilter({
      ...filter,
      ...partialFilter,
    } as FilterTypes.IndividualFilter<RunTypes.Key>);
  };

  const filterValueType = Filters.getFilterValueType(filter);
  switch (filterValueType) {
    case Filters.ValueType.Duration:
      return (
        <FilterValueSelectorTime
          meta={Filters.assertIsValueFilter(filter).meta}
          value={filter.value as string}
          setFilter={setPartial}
          minimumUnit={
            filter.op === 'WITHINSECONDS' ? 'minutes' : null
            /* the withinseconds operation isn't guaranteed to be accurate
                 on the scale of seconds and there isn't a known use case. */
          }
        />
      );
    case Filters.ValueType.Date:
      return (
        <FilterValueSelectorDate
          value={filter.value as string}
          setFilter={setPartial}
        />
      );
    case Filters.ValueType.Tags: {
      return (
        <RunsFilterTagNameSelector
          entityName={entityName}
          projectName={projectName}
          filter={filter as FilterTypes.ValueFilter<RunTypes.Key>}
          setFilter={setPartial}
        />
      );
    }
    case Filters.ValueType.Default:
      if (filter.op === 'CONTAINS') {
        return (
          <RunsFilterContainsInput filter={filter} setFilter={setFilter} />
        );
      }
      return (
        <RunsFilterSimpleValueSelector
          entityName={entityName}
          projectName={projectName}
          filter={filter}
          setFilter={setFilter}
          isValid={isValid}
        />
      );
    default:
      assertUnreachable(filterValueType);
  }
};

type RunsFilterSimpleValueSelectorProps = {
  entityName: string;
  projectName: string;
  filter: FilterTypes.IndividualFilter<RunTypes.Key>;
  setFilter(filter: FilterTypes.IndividualFilter<RunTypes.Key>): void;
  isValid?: boolean;
};
const RunsFilterSimpleValueSelector = (
  props: RunsFilterSimpleValueSelectorProps
) => {
  const {filter, setFilter, isValid = true} = props;
  const extraQueryVariables = useMemo(
    () => ({
      keyPath: Run.keyToServerPath(filter.key),
      filters: JSON.stringify(Filters.toMongo(Filters.EMPTY_FILTERS)),
    }),
    [filter.key]
  );

  const toggleFilter = useCallback(
    (newSelection: string) => {
      const selectionSet = new Set(getCurrentSelections(filter));

      // Individual filters will replace that item with the new item
      if (!Filters.isMultiValue(filter)) {
        if (selectionSet.has(newSelection)) {
          setFilter({
            ...filter,
            value: EMPTY_FILTER_VALUE,
            disabled: true,
          });
        } else {
          setFilter({
            ...filter,
            value: tryJSONParse(newSelection),
            disabled: false,
          });
        }
      }

      // the multi-value filter will add or subtract based on if it's already included
      else {
        if (selectionSet.has(newSelection)) {
          selectionSet.delete(newSelection);
        } else {
          selectionSet.add(newSelection);
        }

        setFilter({
          ...filter,
          value: [...selectionSet].map(tryJSONParse),
          disabled: false,
        });
      }
    },
    [filter, setFilter]
  );

  // We need to reset the state of the infinite dropdown when the filter key changes.
  // Otherwise, the results from the previous key persist.
  // TODO: Do this, but like, better
  const {shouldRenderNull} = useForceRemountOnChange([filter.key]);

  return (
    <TailwindContents>
      {!shouldRenderNull && (
        <FilterSimpleValueInfiniteDropdown
          entityName={props.entityName}
          projectName={props.projectName}
          forceExactMatch={['=', '!='].includes(filter.op)}
          extraQueryVariables={extraQueryVariables}
          skip={extraQueryVariables.keyPath === ``}
          isMultiSelect={Filters.isMultiValue(filter)}
          selections={getCurrentSelections(filter, [])}
          toggleSelection={toggleFilter}
          isValid={isValid}
        />
      )}
    </TailwindContents>
  );
};
